// Filter state (SPEC §6.3). It lives in the URL as typed search params; the API parses the same
// schema from its query string, so a shared URL is a shared view.
import { z } from "zod";
import { REQ_TYPE_VALUES } from "./measures";
import { REGIONS } from "./regions";
import { PO_STAGES, REQ_STAGES } from "./stages";

export const PRESETS = ["procurement", "management", "vessel", "finance"] as const;
export type Preset = (typeof PRESETS)[number];
export const TABS = ["overview", "requisitions", "rfqs", "pos", "spend", "budgets", "addresses", "address-categories", "address-classifications", "country-codes",
  "delivery-conditions", "framework-agreements", "payment-conditions", "items", "item-groups", "item-tree", "measuring-units", "companies", "approval-limits", "currencies", "departments",
  "accounts", "approval-setups", "projects", "purchase-types", "technical-accounts", "vat",
  "class-societies", "flags", "units", "unit-companies", "unit-groups", "vessel-types", "roles", "users", "user-groups", "user-rights",
  "certificates", "certificate-browser", "certificate-groups", "certificate-group-rules", "certificate-locations"] as const;
export type Tab = (typeof TABS)[number];
/** Over budget only with budget access (SPEC §6.3, §12); the API drops it for anyone else. */
export const FLAG_FILTERS = ["open_for_changes", "anomaly", "critical", "over_budget"] as const;
export type FlagFilter = (typeof FLAG_FILTERS)[number];

export const PRESET_LABEL: Record<Preset, string> = {
  procurement: "Procurement",
  management: "Management",
  vessel: "Vessel technical",
  finance: "Finance",
};
export const TAB_LABEL: Record<Tab, string> = {
  overview: "Overview",
  requisitions: "Requisitions",
  rfqs: "RFQs",
  pos: "Purchase orders",
  spend: "Spend",
  budgets: "Budgets",
  addresses: "Address book",
  "address-categories": "Address Category",
  "address-classifications": "Address Classification",
  "country-codes": "Country Code",
  "delivery-conditions": "Delivery Condition",
  "framework-agreements": "Framework Agreement",
  "payment-conditions": "Payment Condition",
  items: "Item",
  "item-groups": "Item Group",
  "item-tree": "Item Tree",
  "measuring-units": "Measuring Unit",
  companies: "Company",
  "approval-limits": "Approval Limit",
  currencies: "Currency",
  departments: "Department",
  accounts: "Account",
  "approval-setups": "Approval Setup",
  projects: "Project",
  "purchase-types": "Purchase Type",
  "technical-accounts": "Technical Account",
  vat: "VAT",
  "class-societies": "Class Society",
  flags: "Flag",
  units: "Unit",
  "unit-companies": "Unit Company",
  "unit-groups": "Unit Group",
  "vessel-types": "Vessel Type",
  roles: "Role",
  users: "User",
  "user-groups": "User Group",
  "user-rights": "User Right",
  certificates: "Certificate",
  "certificate-browser": "Certificate Browser",
  "certificate-groups": "Certificate Group",
  "certificate-group-rules": "Certificate Group Rule",
  "certificate-locations": "Certificate Location",
};
/** Address Book Info's tabs (SPEC §8.7), in Sertica's order. */
export const ADDRESS_TABS = ["addresses", "address-categories", "address-classifications", "country-codes", "delivery-conditions", "framework-agreements", "payment-conditions"] as const satisfies readonly Tab[];
/** Item Info's tabs (SPEC §8.8): of the filters only the search applies, as on the address book. */
export const ITEM_TABS = ["items", "item-groups", "item-tree", "measuring-units"] as const satisfies readonly Tab[];
export const isItemTab = (t: Tab) => (ITEM_TABS as readonly Tab[]).includes(t);
/** Master Data's tabs: Company, Approval Limit, Currency and Department (SPEC §8.9–8.10). */
export const MASTER_TABS = ["companies", "approval-limits", "currencies", "departments"] as const satisfies readonly Tab[];
/** Account Info's tabs (SPEC §8.14), in Sertica's order; Incident and PM/UPM Sub Value have no API. */
export const ACCOUNT_TABS = ["accounts", "approval-setups", "projects", "purchase-types", "technical-accounts", "vat"] as const satisfies readonly Tab[];
/** Unit Info's tabs (SPEC §8.11), and User Info's (SPEC §8.12, admins only). */
export const UNIT_TABS = ["class-societies", "flags", "units", "unit-companies", "unit-groups", "vessel-types"] as const satisfies readonly Tab[];
export const USER_TABS = ["roles", "users", "user-groups", "user-rights"] as const satisfies readonly Tab[];
export const isUserTab = (t: Tab) => (USER_TABS as readonly Tab[]).includes(t);
/** The Sertica right each User Info list needs, as in Sertica (SPEC §8.12). */
export const USER_TAB_RIGHT: Record<(typeof USER_TABS)[number], string> = { roles: "Role", users: "User", "user-groups": "UserGroup", "user-rights": "UserRight" };
/** Certificate Handling's tabs (SPEC §8.13), in Sertica's order. */
export const CERTIFICATE_TABS = ["certificates", "certificate-browser", "certificate-groups", "certificate-group-rules", "certificate-locations"] as const satisfies readonly Tab[];
/** A side-rail section with several tabs: its name and tabs, for the title and the narrow tab bar. */
export function tabSection(t: Tab): { label: string; tabs: readonly Tab[] } | null {
  if ((ADDRESS_TABS as readonly Tab[]).includes(t)) return { label: "Address Book Info", tabs: ADDRESS_TABS };
  if (isItemTab(t)) return { label: "Item Info", tabs: ITEM_TABS };
  if ((MASTER_TABS as readonly Tab[]).includes(t)) return { label: "Master Data", tabs: MASTER_TABS };
  if ((ACCOUNT_TABS as readonly Tab[]).includes(t)) return { label: "Account Info", tabs: ACCOUNT_TABS };
  if ((UNIT_TABS as readonly Tab[]).includes(t)) return { label: "Unit Info", tabs: UNIT_TABS };
  if (isUserTab(t)) return { label: "User Info", tabs: USER_TABS };
  if ((CERTIFICATE_TABS as readonly Tab[]).includes(t)) return { label: "Certificate Handling", tabs: CERTIFICATE_TABS };
  return null;
}
/**
 * What the Sync button reads from Sertica (ARCHITECTURE §6): only the active page's part of the
 * delta. "all" is the whole delta and the budget sync, for the admin page's "Sync now".
 */
/** "full" (admins only): "all", then the nightly reconciliation and the full item read, now. */
export const SYNC_SCOPES = ["all", "procurement", "budgets", "addresses", "items", "master", "account", "unit-user", "certificates", "full"] as const;
export type SyncScope = (typeof SYNC_SCOPES)[number];
export function syncScopeOf(t: Tab): SyncScope {
  if (t === "budgets") return "budgets";
  if ((ADDRESS_TABS as readonly Tab[]).includes(t)) return "addresses";
  if (isItemTab(t)) return "items";
  if ((MASTER_TABS as readonly Tab[]).includes(t)) return "master";
  if ((ACCOUNT_TABS as readonly Tab[]).includes(t)) return "account";
  if ((UNIT_TABS as readonly Tab[]).includes(t) || isUserTab(t)) return "unit-user";
  if ((CERTIFICATE_TABS as readonly Tab[]).includes(t)) return "certificates";
  return "procurement";
}
/** Tabs not about requisitions or POs (SPEC §8.7–8.14): of the filters only the search applies. */
export const isSearchOnlyTab = (t: Tab) => t === "addresses" || !!tabSection(t);
/** Each preset's landing tab (SPEC §6.2). Its default filter (the user's vessels) is applied by the web. */
export const PRESET_TAB: Record<Preset, Tab> = {
  procurement: "requisitions",
  management: "overview",
  vessel: "requisitions",
  finance: "budgets",
};

/** Where a preset lands for this user: never on a locked tab, so Finance without budget access lands on Spend. */
export function landingTab(preset: Preset, canSeeBudgets: boolean): Tab {
  const tab = PRESET_TAB[preset];
  return tab === "budgets" && !canSeeBudgets ? "spend" : tab;
}

const list = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess((v) => (v === undefined || v === "" ? undefined : Array.isArray(v) ? v : [v]), z.array(item).optional());
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const reqStageFilter = z.enum(REQ_STAGES);
export const poStageFilter = z.enum(PO_STAGES);

export const filtersSchema = z.object({
  vessel: list(z.string()),
  type: list(z.enum(REQ_TYPE_VALUES)),
  dept: list(z.string()),
  stage: list(reqStageFilter),
  poStage: list(poStageFilter),
  flags: list(z.enum(FLAG_FILTERS)),
  urgent: z.enum(["yes", "no"]).optional(),
  supplier: list(z.string()),
  /** The supplier's procurement region; on the address book, the address's own. */
  region: list(z.enum(REGIONS)),
  /** "1": the address book lists addresses named "(DO NOT USE)" too; by default it leaves them out. */
  showDoNotUse: z.literal("1").optional(),
  approver: list(z.string()),
  project: list(z.string()),
  from: isoDate.optional(),
  to: isoDate.optional(),
  q: z.string().max(100).optional(),
  // Chart-set filters, shown as removable chips (SPEC §6.3).
  purchaseType: list(z.string()),
  account: list(z.string()),
  age: list(z.coerce.number().int().min(0).max(3)),
  cycle: list(z.coerce.number().int().min(0).max(4)),
});
export type Filters = z.infer<typeof filtersSchema>;
export type FilterKey = keyof Filters;

/** Chart-set filter keys, shown as chips. */
export const CHART_FILTER_KEYS = ["stage", "poStage", "purchaseType", "account", "age", "cycle"] as const satisfies readonly FilterKey[];

/** The full URL search: filters plus the preset and tab. */
export const searchSchema = filtersSchema.extend({
  preset: z.enum(PRESETS).optional(),
  tab: z.enum(TABS).optional(),
  /** The Budgets tab's period: "2026-Q3" or "2026"; absent is the current quarter (SPEC §8.6). */
  period: z
    .string()
    .regex(/^\d{4}(-Q[1-4])?$/)
    .optional(),
  /** "0": the Budgets tab shows the budget's own months, not Sertica's forecast (SPEC §4.3 item 5). */
  forecasts: z.literal("0").optional(),
});
export type Search = z.infer<typeof searchSchema>;

/** Days in the default date range, today included (SPEC §5.4). */
export const DEFAULT_RANGE_DAYS = 180;

/** The last `days` days, today included. */
export function lastDays(today: string, days: number): { from: string; to: string } {
  const from = new Date(`${today}T00:00:00Z`);
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return { from: from.toISOString().slice(0, 10), to: today };
}

/** Default date range: the last 180 days, today included (SPEC §5.4). */
export const defaultRange = (today: string) => lastDays(today, DEFAULT_RANGE_DAYS);

/** The date picker's quick ranges, in days (SPEC §6.3). */
export const RANGE_PRESETS = [30, 60, 90, DEFAULT_RANGE_DAYS] as const;

/** Table sorting and paging for list endpoints. */
export const tableQuerySchema = z.object({
  sort: z.string().max(40).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type TableQuery = z.infer<typeof tableQuerySchema>;

/** Serialises filters to query-string pairs: lists as repeated keys (names may hold commas). */
export function filtersToParams(f: Partial<Search>): URLSearchParams {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) for (const x of v) p.append(k, String(x));
    else p.set(k, String(v));
  }
  return p;
}

const LIST_KEYS = new Set(Object.entries(filtersSchema.shape).filter(([, s]) => s instanceof z.ZodEffects).map(([k]) => k));

/** The inverse of filtersToParams: repeated keys become lists, the rest take their first value. */
export function paramsToObject(params: Record<string, string[]>): Record<string, unknown> {
  const o: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) o[k] = LIST_KEYS.has(k) ? v : v[0];
  return o;
}
